import {Component, OnDestroy, OnInit, ViewChild, AfterViewInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FraisFacturesService} from '@components/admin/frais/factures/frais-factures.service';
import {FloatingButtonAction} from '@share/component/floating-button/floating-button';
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {PageHeaderComponent} from "@share/component/page-header/page-header.component";
import {FournisseursTabsService} from '@services/admin/facture/fournisseurs.tabs.service';
import {BehaviorSubject, Subscription} from 'rxjs';

/**
 * Composant de gestion des détails d'un fournisseur
 *
 * @author Yannick Gauthier
 * @date 15/07/2025
 */
@Component({
    selector: 'factures-fournisseurs-infos',
    templateUrl: './factures-fournisseurs-infos.component.html'
})
export class FacturesFournisseursInfosComponent implements OnInit, OnDestroy, AfterViewInit {
    /** Liste des onglets */
    listeTabItems: Array<PageHeaderItem>;

    /** Liste des actions */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>([]);

    /** Indique si une sauvegarde est en cours */
    isSaving: boolean = false;

    /** Souscription aux actions */
    private actionsSubscription: Subscription;

    /** Souscription aux actions de l'onglet actif */
    private tabActionsSubscription: Subscription;

    /** Référence au composant page-header */
    @ViewChild(PageHeaderComponent) pageHeader: PageHeaderComponent;

    /**
     * Constructeur
     * @param activatedRoute Route activée
     * @param router Service de routage
     * @param fournisseursTabsService Service de gestion des onglets
     * @param fraisFacturesService Service de gestion des frais factures
     */
    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private fraisFacturesService: FraisFacturesService,
        private fournisseursTabsService: FournisseursTabsService
    ) {
    }

    /**
     * Initialisation
     */
    ngOnInit(): void {
        //Récupération de l'id du fournisseur dans l'URL
        const idFournisseur = this.activatedRoute.snapshot.params.id;

        //Initialisation des onglets
        this.listeTabItems = [{
            code: TypeOnglet.GENERALITES,
            libelle: 'admin.frais.fournisseurs.infos.generalites',
            url: 'Admin/Frais/Factures/Fournisseurs/' + idFournisseur + '/Generalites',
            selected: false
        }, {
            code: TypeOnglet.CONTRAT,
            libelle: 'admin.frais.fournisseurs.infos.contrat',
            url: 'Admin/Frais/Factures/Fournisseurs/' + idFournisseur + '/Contrat',
            selected: false
        }];

        //Vérifier le fragment dans l'URL pour activer l'onglet correspondant
        this.activatedRoute.fragment.subscribe(fragment => {
            if (fragment) {
                //Recherche de l'onglet correspondant au fragment
                const tabIndex = this.listeTabItems.findIndex(item => item.code === fragment);
                if (tabIndex >= 0) {
                    //Marquer cet onglet comme sélectionné
                    this.listeTabItems.forEach((tab, index) => {
                        tab.selected = (index === tabIndex);
                    });
                    
                    //Mettre à jour l'onglet actif dans le service
                    this.fournisseursTabsService.setActiveTabCode(fragment);
                }
            }
        });

        //Indique au composant Frais Factures qu'on affiche le détail d'un fournisseur
        this.fraisFacturesService.isShowDetail = true;

        //Souscription aux actions de l'onglet actif
        this.tabActionsSubscription = this.fournisseursTabsService.getActiveTabActions$().subscribe(actions => {
            if (actions && actions.length > 0) {
                this.listeActions.next(actions);
            }
        });

        //Souscription aux actions du fournisseur
        this.actionsSubscription = this.fournisseursTabsService.listeActions.subscribe(actions => {
            // Ne mettre à jour les actions que si l'onglet actif est GENERALITES
            if (this.fournisseursTabsService.getActiveTabCode() === TypeOnglet.GENERALITES) {
                this.listeActions.next(actions);
            }
        });
    }

    /**
     * Gestion du changement d'onglet
     * @param item Onglet sélectionné
     */
    onTabChange(item: PageHeaderItem): void {
        if (item && item.url) {
            //Mettre à jour l'onglet actif dans le service
            this.fournisseursTabsService.setActiveTabCode(item.code);
            
            //Naviguer vers l'URL de l'onglet
            this.router.navigateByUrl(item.url);
        }
    }

    /**
     * Retour à la liste des fournisseurs
     */
    goBack(): void {
        this.router.navigate(['Admin/Frais/Factures/Fournisseurs']);
    }

    /**
     * Hook après initialisation du composant
     */
    ngAfterViewInit(): void {
        //Vérifier le fragment dans l'URL pour activer l'onglet correspondant
        this.activatedRoute.fragment.subscribe(fragment => {
            if (fragment && this.pageHeader) {
                //Recherche de l'onglet correspondant au fragment
                const tabIndex = this.listeTabItems.findIndex(item => item.code === fragment);
                if (tabIndex >= 0) {
                    //Mettre à jour l'onglet actif dans le service
                    this.fournisseursTabsService.setActiveTabCode(fragment);
                    
                    //Utiliser la méthode changeTab du composant page-header
                    setTimeout(() => {
                        this.pageHeader.changeTab(fragment);
                    });
                }
            }
        });
    }

    /**
     * Hook destruction
     */
    ngOnDestroy(): void {
        //Indique au composant Frais Factures qu'on n'affiche plus le détail d'un fournisseur
        this.fraisFacturesService.isShowDetail = false;

        //Désabonnement
        if (this.actionsSubscription) {
            this.actionsSubscription.unsubscribe();
        }
        if (this.tabActionsSubscription) {
            this.tabActionsSubscription.unsubscribe();
        }
    }
}

/**
 * Enum pour les types d'onglets du fournisseur
 */
export enum TypeOnglet {
    GENERALITES = 'GENERALITES',
    CONTRAT = 'CONTRAT'
}
